'use client'

import * as React from 'react'
import { X } from 'lucide-react'

import { ImageUpload } from './image-upload'
import { Button } from '@/components/ui/button'
import { deleteFile } from "@/lib/images"
import { cn } from "@/lib/utils"

interface FormImageUploadProps {
    id: string
    name: string
    url: string | undefined | null
    onImageChange: (url: string | undefined) => void
    className?: string
    children?: React.ReactNode
}

export const FormImageUpload = ({
    id,
    name,
    url: initialUrl,
    onImageChange,
    className,
    children,
}: FormImageUploadProps) => {
    const [url, setUrl] = React.useState<string | null>(initialUrl ?? null)

    const handleRemove = async () => {
        if (url) {
            setUrl(null)
            onImageChange(undefined)
            try {
                await deleteFile(url)
            } catch {
                // Ignore error
            }
        }
    }

    const handleImageAvailable = (newUrl: string) => {
        setUrl(newUrl)
        onImageChange(newUrl)
    }

    return (
        <div className={cn('flex flex-none gap-4', className)}>
            {url ? (
                <div className="relative">
                    <div className="h-[60px] w-[60px] rounded-lg border overflow-hidden">
                        <img
                            src={url}
                            alt="Uploaded"
                            className="h-full w-full object-contain"
                        />
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleRemove}
                        className="absolute right-[-8px] top-[-8px] h-5 w-5 rounded-full bg-background border shadow-sm"
                    >
                        <X className="h-3 w-3" />
                    </Button>
                </div>
            ) : (
                <div className="flex h-[60px] w-[60px] items-center justify-center rounded-lg border bg-muted">
                    <span className="text-xs text-muted-foreground">No image</span>
                </div>
            )}

            <div className="flex-1">
                <ImageUpload
                    id={id}
                    name={name}
                    url={url}
                    onImageAvailable={handleImageAvailable}
                    className="h-9"
                />
                {!url && children}
            </div>
        </div>
    )
}

